import { inject, Injectable } from '@angular/core';
import { Params, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, Observable, of, switchMap, take, timeout } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';
import { LocaleService } from '@markant-platform/shared/util-locale';

@Injectable({
  providedIn: 'root',
})
export class LoginGuard {
  private readonly REDIRECT_URI_KEY = 'redirectURI';
  private readonly REDIRECT_SEARCH_PARAMS_KEY = 'redirectSearchParams';
  private readonly REGISTRATION_COMPLETE_PATH = '/registration/complete';
  private readonly PLATFORM_PATH = '/platform';
  private readonly REDIRECT_TO_LOGIN_ERRORS = ['missing_refresh_token', 'login_required'];

  private readonly authService = inject(AuthService);
  private readonly localeService = inject(LocaleService);
  private readonly router = inject(Router);

  /**
   * When not authenticated the login-flow is triggered with the currently stored language.
   * If the user is authenticated and wanted to navigate to a deep link. The user is redirected to the deeplink after login.
   *
   * @returns {Observable<boolean>} - true when user is authenticated, false when not. Returns URLTree when user has called a deep link
   */
  canActivate(state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authService.isAuthenticated$.pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return this.authService.error$.pipe(
            take(1),
            //timeout triggers if reason for not authenticated is not an auth0 error
            timeout(0),
            catchError(() => of(undefined)),
            switchMap(error => {
              if (error) {
                const { message } = error;

                if ('error' in error && this.REDIRECT_TO_LOGIN_ERRORS.includes(error['error'] as string)) {
                  this.redirectToLogin(state);
                  return of(false);
                } else {
                  return this.router.navigate(['error'], {
                    queryParams: {
                      reason: message,
                      error_code: message,
                      logout: true,
                    },
                  });
                }
              }

              this.redirectToLogin(state);

              return of(false);
            })
          );
        }

        const redirectURI = localStorage.getItem(this.REDIRECT_URI_KEY) ?? '';

        if (redirectURI) {
          return this.redirectToStoredURI(redirectURI);
        }

        return of(isAuthenticated);
      })
    );
  }

  private redirectToStoredURI(redirectURI: string): Observable<UrlTree> {
    const redirectSearchParams = localStorage.getItem(this.REDIRECT_SEARCH_PARAMS_KEY) ?? '';

    // remove redirection url from storage to prevent multiple executions
    localStorage.removeItem(this.REDIRECT_URI_KEY);
    localStorage.removeItem(this.REDIRECT_SEARCH_PARAMS_KEY);

    const queryParams = this.getSearchParams(redirectSearchParams);

    return of(this.router.createUrlTree([redirectURI], { queryParams }));
  }

  private redirectToLogin(state: RouterStateSnapshot): void {
    // look up current url and remove '/platform'-prefix (base URL) for valid angular path
    const redirectURI = location.pathname.replace(this.PLATFORM_PATH, '');
    const redirectSearchParams = location.search;

    if ((redirectURI !== '/' || redirectSearchParams) && redirectURI !== this.REGISTRATION_COMPLETE_PATH) {
      localStorage.setItem(this.REDIRECT_URI_KEY, redirectURI);
      localStorage.setItem(this.REDIRECT_SEARCH_PARAMS_KEY, redirectSearchParams);
    }

    let storedLanguage = localStorage.getItem('language') ?? 'en-GB';

    if (storedLanguage) {
      storedLanguage = this.localeService.changeLanguageCodeToAdaptAuth0AvailableLanguages(storedLanguage);
    }

    this.authService.loginWithRedirect({
      appState: { target: state.url },
      authorizationParams: {
        ui_locales: storedLanguage,
      },
    });
  }

  private getSearchParams(searchParams: string): Record<string, string> {
    if (searchParams) {
      const queryParams: Params = {};

      const urlSearchParams = new URLSearchParams(searchParams);

      urlSearchParams.forEach((value, key) => {
        queryParams[key] = value;
      });

      return queryParams;
    }

    return {};
  }
}
